/* eslint-disable rxjs/no-subject-value */
import { Directive, Input, OnDestroy } from '@angular/core';
import { WorkflowItemDirective } from '@app/workflow/directives/workflow-item/workflow-item.directive';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import { isEqual } from 'lodash';
import { BehaviorSubject, EMPTY, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, shareReplay, switchMap } from 'rxjs/operators';

export type TDataSelector = 'inprogress' | 'completed' | 'auto' | SessionItemData | Record<string, unknown>;

/**
 * Direktive zur Auswahl und zum Überschreiben der Daten aus WorkflowItemDirective.
 *
 * Die Direktive erlaubt folgende Konfigurationen:
 *
 * * 'inprogress': nicht-abgeschlossener Workflowschritt
 * * 'completed': abgeschlossener Workflowschritt
 * * 'auto': automatische Auswahl anhand des Workflowschritt-Status
 * * SessionItemData: unabhängiges SessionItemData Objekt
 * * objekt: unabhängiges Daten-Objekt
 *
 * @example
 * <div [appWorkflowItem]="workflowItem" appWorkflowItemData="auto"></div>
 * <div [appWorkflowItem]="workflowItem" [appWorkflowItemData]="{ somekey: 'foo' }"></div>
 * <div [appWorkflowItem]="workflowItem" [appWorkflowItemData]="mySessionItemData"></div>
 */
@Directive({
	selector: '[appWorkflowItemData]'
})
export class WorkflowItemDataDirective implements OnDestroy {

	readonly data$: Observable<SessionItemData>;

	private dataSelector$ = new BehaviorSubject<TDataSelector>('inprogress');
	private data: SessionItemData;
	private subscription = new Subscription();


	@Input('appWorkflowItemData')
	set dataSelector(dataSelector: TDataSelector) {
		this.dataSelector$.next(dataSelector);
	}

	get dataSelector(): TDataSelector {
		return this.dataSelector$.value;
	}

	constructor(
		public workflowItem: WorkflowItemDirective
	) {
		this.data$ = this.dataSelector$.pipe(
			switchMap(dataSelector => this.getData(dataSelector)),
			shareReplay({ refCount: true, bufferSize: 1 }),
		);
		this.subscription.add(this.data$.subscribe(data => this.data = data));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.dataSelector$.complete();
	}

	private getData(dataSelector: TDataSelector): Observable<SessionItemData> {
		if (dataSelector === undefined || dataSelector === null) {
			console.log(`dataSelector undefined. Falling back to 'auto'`);
			return this.workflowItem.current$;
		}
		if (typeof dataSelector === 'string') {
			switch (dataSelector) {
				case 'inprogress':
					return this.workflowItem.item$;
				case 'completed':
					return this.workflowItem.itemFull$;
				case 'auto':
					return this.workflowItem.current$;
				default:
					console.log(`unknown dataSelector '${dataSelector}'. Falling back to 'auto'`);
					return this.workflowItem.current$;
			}
		}
		if (dataSelector instanceof SessionItemData) {
			return of(dataSelector as SessionItemData);
		}
		if (typeof dataSelector === 'object') {
			return of(new SessionItemData(dataSelector));
		}
		return this.workflowItem.item$;
	}

	property$(key: string | string[]): Observable<unknown> {
		return this.data$.pipe(
			switchMap(item => item ? item.property$(key) : EMPTY),
			distinctUntilChanged(isEqual),
		);
	}

	propertyFull$(key: string | string[]): Observable<unknown> {
		return this.workflowItem.propertyFull$(key);
	}

	getProperty(key: string | string[]): unknown {
		return this.data?.getProperty(key);
	}

	getPropertyFull(key: string | string[]): unknown {
		return this.workflowItem.getPropertyFull(key);
	}

	setProperty(key: string | string[], value: unknown): void {
		this.data?.setProperty(key, value === '' ? null : value);
	}

	get item(): SessionItemData {
		return this.data;
	}

	/**
	 * @deprecated Ignoriert ein eventuell gesetztes [data] Feld
	 */
	get itemFull(): SessionItemData {
		return this.workflowItem.itemFull;
	}

	/**
	 * @deprecated use data$
	 */
	get item$(): Observable<SessionItemData> {
		return this.data$;
	}

	/**
	 * @deprecated Ignoriert ein eventuell gesetztes [data] Feld
	 */
	get itemFull$(): Observable<SessionItemData> {
		return this.workflowItem.itemFull$;
	}

}
